import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authChangeMyPassword, setAdminToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
};

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isOwner = user?.role === "owner";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit = !busy && !!current && next.length >= 8 && confirm === next && next !== current;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authChangeMyPassword(current, next);
      setOk(true);
      // Force a clean re-login so the new password is used. Token may still be
      // valid, but signing out is the safer UX so the user verifies the change.
      setTimeout(async () => {
        setAdminToken(null);
        try { await logout(); } catch { /* logout already redirects */ }
        navigate("/admin/login", { replace: true });
      }, 1500);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="change-password-page">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>SECURITY · YOUR ACCOUNT</div>
        <h1 style={h1}>CHANGE PASSWORD</h1>

        {isOwner ? (
          <div data-testid="owner-cannot-self-change">
            <p style={{ color: NAVY, fontSize: 16, lineHeight: 1.6 }}>
              The owner password is rotated via the server&apos;s <code style={codeStyle}>.env</code> file
              for an extra layer of protection.
            </p>
            <ol style={{ color: "#5b5e69", lineHeight: 1.8, paddingLeft: 18, marginTop: 14 }}>
              <li>SSH or open a terminal on the backend and edit <code style={codeStyle}>OWNER_PASSWORD</code> in <code style={codeStyle}>/app/backend/.env</code>.</li>
              <li>Run <code style={codeStyle}>sudo supervisorctl restart backend</code>.</li>
              <li>Log out and sign back in with the new password.</li>
            </ol>
            <button onClick={() => navigate("/admin/users")} style={btnSecondary} data-testid="owner-back-btn">
              ← BACK TO USER LIST
            </button>
          </div>
        ) : ok ? (
          <div data-testid="change-success" style={{ background: "#E8F5EE", color: "#1A7A4A", padding: 18, borderRadius: 12 }}>
            <strong>Password updated.</strong> Signing you out so you can confirm it works…
          </div>
        ) : (
          <form onSubmit={submit}>
            <p style={{ color: "#5b5e69", margin: "0 0 18px" }}>
              Signed in as <strong>{user?.email}</strong>. Choose something at least 8 characters long.
            </p>

            <Label>CURRENT PASSWORD</Label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="current-password-input"
              style={inputStyle}
            />

            <Label>NEW PASSWORD</Label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="new-password-input"
              style={inputStyle}
            />
            {tooShort && <Hint kind="warn">Must be at least 8 characters.</Hint>}
            {sameAsCurrent && <Hint kind="warn">New password must be different from the current one.</Hint>}

            <Label>CONFIRM NEW PASSWORD</Label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="confirm-password-input"
              style={inputStyle}
            />
            {mismatch && <Hint kind="warn">Passwords don&apos;t match.</Hint>}

            {error && <div data-testid="change-password-error" style={errBox}>{error}</div>}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="change-password-submit"
              style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.5 }}>
              {busy ? "Updating…" : "UPDATE PASSWORD"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={btnGhost}
              data-testid="change-password-cancel">
              Cancel
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 6, marginTop: 4 }}>{children}</div>
);

const Hint = ({ children, kind }) => (
  <div style={{
    color: kind === "warn" ? "#A32D2D" : "#5b5e69",
    background: kind === "warn" ? "#FDECEC" : "transparent",
    fontSize: 13, padding: kind === "warn" ? "6px 10px" : 0, borderRadius: 8,
    margin: "0 0 14px",
  }}>{children}</div>
);

const pageWrap = { background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 480, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 38, color: NAVY, margin: "4px 0 22px" };
const inputStyle = { width: "100%", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, marginBottom: 14, outline: "none", boxSizing: "border-box" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 8 };
const btnSecondary = { display: "inline-block", marginTop: 22, background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, padding: "12px 22px", borderRadius: 10, fontWeight: 700, letterSpacing: 2, cursor: "pointer" };
const btnGhost = { width: "100%", background: "transparent", color: NAVY, border: "none", padding: "10px", marginTop: 4, cursor: "pointer", fontSize: 13 };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
const codeStyle = { background: BG, padding: "2px 8px", borderRadius: 6, fontFamily: "monospace", fontSize: 13, color: NAVY };
